import React, { useEffect, useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Select,
  MenuItem,
  Tooltip,
  Divider,
  Drawer,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchBar from "../../components/SearchBar";
import ChatIcon from "@mui/icons-material/Chat";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { useGetBookingStatsQuery, useLazyBookingExportCsvQuery, useLazyGetBookingListQuery, useUpdateBookingStatusMutation } from "../../service/booking";
import { showError, showToast } from "../../constants/toasts";
import Loader from "../../constants/Loader";
import { SelectChangeEvent } from "@mui/material";
import { ORDER_STATUS, PAYMENT_TYPE } from "../../utils/enums";
import CustomPagination from "../../components/CustomPagination";
import { useCreateRoomMutation } from "../../service/chat";
import { toast } from "react-toastify";
import CurrencyIcon from "../../components/dirham";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// No static rows needed anymore

const isOptionDisabled = (currentStatusStr: number | string | undefined, optionValue: number) => {
  if (!currentStatusStr) return false;
  const currentStatus = Number(currentStatusStr);
  if (currentStatus === ORDER_STATUS.PENDING) {
    return ![ORDER_STATUS.PENDING, ORDER_STATUS.ACCEPT, ORDER_STATUS.CANCELLED].includes(optionValue);
  }
  if (currentStatus === ORDER_STATUS.ACCEPT) {
    return ![ORDER_STATUS.ACCEPT, ORDER_STATUS.ONGOING, ORDER_STATUS.CANCELLED].includes(optionValue);
  }
  if (currentStatus === ORDER_STATUS.ONGOING) {
    return ![ORDER_STATUS.ONGOING, ORDER_STATUS.COMPLETED].includes(optionValue);
  }
  if (currentStatus === ORDER_STATUS.COMPLETED) {
    return optionValue !== ORDER_STATUS.COMPLETED;
  }
  if (currentStatus === ORDER_STATUS.CANCELLED) {
    return optionValue !== ORDER_STATUS.CANCELLED;
  }
  return false;
};


const OrderManagement: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [valueTabs, setValueTabs] = useState<number>(0);
  const navigate = useNavigate();

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
    setPage(1);
  };
  const [getBookingList] = useLazyGetBookingListQuery();
  const [updateBookingStatus] = useUpdateBookingStatusMutation();
  const [exportCsv] = useLazyBookingExportCsvQuery();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [bookingList, setBookingList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [paymentType, setPaymentType] = useState<string>("All");
  const [createRoom, { isLoading: createRoomLoading }] =
    useCreateRoomMutation();
  const { data: statsData, refetch: refetchStats } = useGetBookingStatsQuery();
  const stats = statsData?.data || {};

  const handleCreateRoom = async (userId: number | undefined) => {
    if (!userId) return;
    try {
      const body = {
        userId: userId,
        type: 2,
      };

      const res = await createRoom(body).unwrap();
      if (res?.statusCode === 200) {
        toast.success(res?.message || "");
        navigate(`/chat?roomId=${res?.data?.id}`);
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.data?.message || "Something went wrong");
    }
  };

  const getBookingListApi = async () => {
    const getStatusValue = (index: number) => {
      switch (index) {
        case 1: return ORDER_STATUS.PENDING;
        case 2: return ORDER_STATUS.ACCEPT;
        case 3: return ORDER_STATUS.ONGOING;
        case 4: return ORDER_STATUS.CANCELLED;
        case 5: return ORDER_STATUS.COMPLETED;
        default: return undefined;
      }
    };
    try {
      setLoading(true);
      const response = await getBookingList({
        page: page,
        limit: 10, // Always use a fixed limit for server-side pagination
        search: debouncedSearchTerm,
        paymentType: paymentType === "All" ? undefined : Number(paymentType),
        status: valueTabs === 0 ? undefined : Number(getStatusValue(valueTabs)), // Pass status for filtered tabs
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setBookingList(response?.data?.list || []);
        setCount(response?.data?.total || 0);
      } else {
        setBookingList([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const handleStatusChange = async (
    event: SelectChangeEvent<any>,
    bookingId: string,
  ) => {
    const newStatus = event.target.value;
    try {
      const response = await updateBookingStatus({
        id: bookingId,
        body: { status: Number(newStatus) },
      }).unwrap();

      if (response?.statusCode === 200) {
        showToast("Status Updated Successfully");
        getBookingListApi();
        refetchStats();
      } else {
        showError("Failed to update status");
      }
    } catch (error: any) {
      showError(error?.data?.message || "Something went wrong");
    }
  };

  const handleExportCsv = async () => {
    const getStatusValue = (index: number) => {
      switch (index) {
        case 1: return ORDER_STATUS.PENDING;
        case 2: return ORDER_STATUS.ACCEPT;
        case 3: return ORDER_STATUS.ONGOING;
        case 4: return ORDER_STATUS.CANCELLED;
        case 5: return ORDER_STATUS.COMPLETED;
        default: return undefined;
      }
    };

    try {
      setLoading(true);
      const res = await exportCsv({
        search: debouncedSearchTerm,
        paymentType: paymentType === "All" ? undefined : Number(paymentType),
        status: valueTabs === 0 ? undefined : Number(getStatusValue(valueTabs)),
      }).unwrap();
      setLoading(false);

      const downloadUrl = res?.data?.url || res?.data;
      if (downloadUrl && typeof downloadUrl === "string") {
        window.open(downloadUrl, "_blank");
      } else {
        showError("Export URL not found");
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "Failed to export CSV");
    }
  };

  useEffect(() => {
    getBookingListApi();
  }, [page, debouncedSearchTerm, paymentType, valueTabs]);


  const CustomTabPanel: React.FC<TabPanelProps> = ({
    children,
    value,
    index,
  }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );

  const a11yProps = (index: number) => ({
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  });

  const tabStatus = ["All", "Pending", "Accept", "Ongoing", "Cancelled", "Past"];

  const getStatusLabel = (status: string | number) => {
    switch (Number(status)) {
      case ORDER_STATUS.PENDING:
        return "Pending";
      case ORDER_STATUS.ACCEPT:
        return "Accept";
      case ORDER_STATUS.ONGOING:
        return "Ongoing";
      case ORDER_STATUS.COMPLETED:
        return "Past";
      case ORDER_STATUS.CANCELLED:
        return "Cancelled";
      default:
        return "Unknown";
    }
  };

  const getTabCount = () => {
    return count;
  };

  return (
    <>
      <Loader isLoad={loading} />
      <div className="main_title">
        <h1>My Bookings</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - My
          Bookings
        </p>
      </div>
      <div className="stats_boxes gap_m">
        <div className="cards">
          <h2>{stats?.total || 0}</h2>
          <p>{stats?.total || 0} Bookings (This Month)</p>
          <figure className="sucess">
            <img src="/static/images/bag.svg" alt="Users" />
          </figure>
        </div>

        <div className="cards">
          <figure className="active_order">
            <img src="/static/images/active_order.svg" alt="Active Users" />
          </figure>
          <h2>{stats?.pending || 0}</h2>
          <p>Pending Bookings</p>
        </div>

        <div className="cards">
          <figure className="warning">
            <img src="/static/images/reward_icon.svg" alt="Revenue" />
          </figure>
          <h2>{stats?.completed || 0}</h2>
          <p>Completed</p>
        </div>
        <div className="cards">
          <figure className="blue">
            <img src="/static/images/star.svg" alt="Revenue" />
          </figure>
          <h2><CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} />{stats?.awaitingPayouts || 0}</h2>
          <p>Awaiting Payout</p>
        </div>
        <div className="cards">
          <figure className="blue">
            <img src="/static/images/star.svg" alt="Revenue" />
          </figure>
          <h2>{stats?.cancelled || 0} </h2>
          <p>Cancelled Bookings</p>
        </div>
      </div>
      {/* Tabs */}
      <Box className="custom_tabs1" sx={{ width: "100%", mt: 2 }}>
        <Tabs
          value={valueTabs}
          onChange={handleChangeTabs}
          aria-label="order management tabs"
          className="custom_tabs_links"
        >
          {tabStatus.map((status, index) => (
            <Tab key={status} label={status} {...a11yProps(index)} />
          ))}
        </Tabs>
      </Box>

      <div className="cards order_wrap">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              value={searchTerm}
              onChange={(e: any) => setSearchTerm(e.target.value)}
              onCross={() => {
                setSearchTerm("");
                setDebouncedSearchTerm("");
              }}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              placeholder="Search by ID, Vendor or Service..."
            />
            <div className="form">
              <div className="control_group">
                <Select
                  fullWidth
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                >
                  <MenuItem disabled value="default">
                    Payment Type
                  </MenuItem>
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value={PAYMENT_TYPE.CASH}>Cash</MenuItem>
                  <MenuItem value={PAYMENT_TYPE.ONLINE}>Online</MenuItem>
                  <MenuItem value={PAYMENT_TYPE.OTHER}>Other</MenuItem>
                </Select>
              </div>
            </div>
          </div>
          <div className="right_s">
            <Button className="btn btn_primary" onClick={handleExportCsv}>
              <DownloadIcon /> Export CSV
            </Button>
          </div>
        </div>

        {/* Table Panels */}
        {tabStatus.map((status, index) => (
          <CustomTabPanel key={index} value={valueTabs} index={index}>
            <TableContainer className="table_container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Booking ID</TableCell>
                    <TableCell>Vendor Detail</TableCell>
                    <TableCell>Booking Date/Time</TableCell>
                    <TableCell>Net Payout</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell>Category Name</TableCell>
                    <TableCell>Service Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookingList.length > 0 ? (
                    bookingList.map((row, index) => (
                      <TableRow hover key={index}>
                        {/* Booking ID */}
                        <TableCell>#{row?.bookingId}</TableCell>

                        {/* Customer Detail */}
                        <TableCell>
                          <div className="user_block">
                            <figure>
                              <img src={row?.vendor?.logoMedia?.imageUrl || row?.vendor?.logo || row?.vendor?.image || "/static/images/user_placeholder.png"} alt="" />
                            </figure>
                            <p>
                              <b>{row?.vendor?.storeName || "N/A"}</b>
                              <span>ID: {row?.vendor?.id}</span>
                            </p>
                          </div>
                        </TableCell>

                        {/* Booking Date/Time */}
                        <TableCell>{new Date(row?.date).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</TableCell>

                        {/* Amount */}
                        <TableCell><CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} /> {row?.netPayout}</TableCell>

                        {/* Amount */}
                        <TableCell><CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} /> {row?.totalAmount}</TableCell>

                        {/* Payment */}
                        <TableCell>{"Card"}</TableCell>

                        {/* Category Name */}
                        <TableCell>{row?.service?.category?.name || "N/A"}</TableCell>

                        {/* Service Name */}
                        <TableCell>{row?.service?.name || "N/A"}</TableCell>

                        {/* Status */}
                        <TableCell className="form">
                          <Select
                            size="small"
                            value={row?.status ? Number(row.status) : ""}
                            onChange={(e) => handleStatusChange(e, row?._id || row?.id)}
                          >
                            <MenuItem value={ORDER_STATUS.PENDING} disabled={isOptionDisabled(row?.status, ORDER_STATUS.PENDING)}>Pending</MenuItem>
                            <MenuItem value={ORDER_STATUS.ACCEPT} disabled={isOptionDisabled(row?.status, ORDER_STATUS.ACCEPT)}>Accept</MenuItem>
                            <MenuItem value={ORDER_STATUS.ONGOING} disabled={isOptionDisabled(row?.status, ORDER_STATUS.ONGOING)}>Ongoing</MenuItem>
                            <MenuItem value={ORDER_STATUS.COMPLETED} disabled={isOptionDisabled(row?.status, ORDER_STATUS.COMPLETED)}>Completed</MenuItem>
                            <MenuItem value={ORDER_STATUS.CANCELLED} disabled={isOptionDisabled(row?.status, ORDER_STATUS.CANCELLED)}>Cancelled</MenuItem>
                          </Select>
                        </TableCell>

                        {/* Action */}
                        <TableCell>
                          <Box className="table_actions v2">
                            <Tooltip title="Chat">
                              <IconButton onClick={() => handleCreateRoom(row.vendorId)}>
                                <ChatIcon color="action" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View">
                              <IconButton
                                onClick={() => navigate(`/order-management/view/${row?._id || row?.id}`, { state: { fromOngoing: tabStatus[valueTabs] === "Ongoing" } })} >
                                <VisibilityIcon color="action" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No bookings found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>

              </Table>
            </TableContainer>
          </CustomTabPanel>
        ))}
      </div>

      {bookingList?.length ? (
        <CustomPagination
          currentPage={page - 1}
          count={getTabCount()}
          pageSize={10}
          onPageChange={(page) => setPage(page + 1)}
        />
      ) : null}
    </>
  );
};

export default OrderManagement;
